import React from 'react'
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionItem as Item } from '@szhsin/react-accordion';

// Icons
import PersonWorkspaceIcon from 'assets/icons/PersonWorkspaceIcon';
import PeopleIcon from 'assets/icons/PeopleIcon';
import AlarmClockIcon from 'assets/icons/AlarmClockIcon';
import KeyboardArrowDownIcon from 'assets/icons/KeyboardArrowIcon';

function LeadershipSustainability() {
  const { t } = useTranslation();

  const AccordionItem = ({ header, order, ...rest }) => {
    return (
      <Item
        {...rest}
        header={({ state: { isEnter } }) => (
          <div className='flex flex-row justify-between gap-6 md:gap-8 xl:gap-10'>
            <div>
              <p className='font-bold text-sm sm:text-lg md:text-2xl xl:text-3xl'>{header}</p>
            </div>
            <div>
              <KeyboardArrowDownIcon
                className={`h-4 md:h-6 xl:h-8 w-4 md:w-6 xl:w-8 ml-auto fill-black transition-transform duration-200 ease-out ${isEnter && "rotate-180"}`}
              />
            </div>
          </div>
        )}
        className={''}
        buttonProps={{
          className: ({ isEnter }) =>
            `w-full sm:p-4 text-left`
        }}
        contentProps={{
          className: "transition-height duration-200 ease-out"
        }}
        panelProps={{ className: "sm:p-4" }}
        initialEntered={true}
      />
    )
  };

  const leadership_contents = [
    {
      title: 'The Board’s Role in ESG & Sustainability',
      img_url: 'https://storage.googleapis.com/testing_storage_cloudxier/cloudxier-images/8694d49085ddfc456630948912502e0581437472d18d0dc7766326b28502d7be656aab5680a883ce27f51d9fea62160ce90ad75a02d72666e356c99e7738704b1fb463c6021a31dc931710bdca14bc7609359271870c6354b407467c9b78ce004e72f95131808c7ea0a13fbe146847d3a1194cf5068d1543e018/rez-file-1740639550629.jpg',
      descriptions: [
        'The growing importance of ESG in corporate governance.',
        'Why sustainability is a strategic imperative for businesses.',
        'Board-level oversight: Fiduciary duties, accountability, and leadership in ESG.',
        'Key frameworks: UN SDGs, TCFD, TNFD, GRI, ISSB, and regulatory requirements (SEC, EU CSRD).'
      ]
    },
    {
      title: 'ESG Risks & Opportunities – Strategic Insights for Boards',
      img_url: 'https://storage.googleapis.com/testing_storage_cloudxier/cloudxier-images/9d3575995afc5353f4b023ecaf1bc008ff7c56ce72f91e884c79d3f70060657ab8a7c01dbae84d2f8eb1ee95757f4d620911fd6b86941bf4c13dcf10055d28983aa5b11bed428d3a17b95dbf83e690fe3c262461428921b294e78ea26e916badb8530b2a00ae4c7422d79f202a74554daed94a91f6454fc5a594/file-1740382757067.png',
      descriptions: [
        'ESG as a driver of long-term business resilience and competitive advantage.',
        'Identifying ESG risks: Climate change, regulatory compliance, supply chain, reputational risks.',
        'ESG opportunities: Sustainable finance, green investments, innovation, stakeholder trust.',
        'Case study: How ESG failures have impacted companies.'
      ]
    },
    {
      title: 'ESG in Action – Embedding Sustainability into Corporate Strategy',
      img_url: 'https://storage.googleapis.com/testing_storage_cloudxier/cloudxier-images/5fbfb0c515cf8662d4845258f1671379e152012238d7be1717e274d6f63d37dd9b4589b96d7765385b9e0df6d6d1b3bb3a342524e743d1364355a6fa9dec41ea9a3b90ff852fd916c1ffb43c3fa19097e72a19e079d99577268cd3afd55f292454808e3a9374abf9c0d7f87933c787c3d32fb665a2e3c7bc156f/file-1740382568117.png',
      descriptions: [
        'Aligning ESG with corporate mission, strategy, and financial performance.',
        'The role of leadership in setting sustainability targets & integrating ESG into decision-making.',
        'How to drive ESG adoption across the organization (culture, incentives, reporting).',
        'Case study: Companies successfully integrating ESG into their strategy'
      ]
    },
    {
      title: 'ESG Reporting, Disclosure & Stakeholder Expectations',
      img_url: 'https://storage.googleapis.com/testing_storage_cloudxier/cloudxier-images/86e89da0dfb40885c294547355bc6e98f8c7d77b7d9bf2facc77898203229ab2dc7ac197a6b03cee7ce513d83983843f88bfa87b1ad61aa762e9354c9fa0bfda6c561ada2c054e2717b95e66349b8c27e247623e1dc09de23f09004b2ed2cfd45f90e8288b2866db41e94f9e28bfb8690dbb85be8fd50b06f69b/rez-file-1740639841443.png',
      descriptions: [
        'Understanding ESG reporting requirements (TCFD, TNFD, CSRD, GRI, SASB).',
        'Transparency & disclosure: The importance of ESG data and reporting accuracy.',
        'Managing investor and stakeholder expectations on ESG performance.',
        'Greenwashing risks and how to ensure credible ESG commitments.'
      ]
    },
    {
      title: 'Interactive Boardroom Discussion & Action Planning',
      img_url: 'https://storage.googleapis.com/testing_storage_cloudxier/cloudxier-images/cfe3ae74ee0665c3e75dc6b29f168addaabd40bc661f62728a1c88ab4197721b2fc76159480d03a2a85a761bc7d517cfa8e533fb38a15828669030e99e8dd8e0a6a02fc9006d90ac08281f87995849ce7baa7ca9ac6095400a139f11fc451a55ceb2e30b269e1a4e6dc4f005b922b76dfc9feb83e722edd6d272/file-1740381949629.png',
      descriptions: [
        'Scenario Exercise.',
        'Breakout Discussion.',
        'Action Plan.'
      ]
    }
  ]

  const objectives = [
    'Understand the board’s role in driving sustainability and ESG strategy.',
    'Recognize key risks and opportunities associated with ESG governance.',
    'Learn how to integrate ESG principles into corporate decision-making.',
    'Identify critical ESG reporting and compliance requirements.',
    'Gain actionable insights for effective ESG leadership at the board level.',
  ]

  const takeways = [
    'Understanding the board’s legal, financial, and ethical responsibilities in ESG.',
    'Board members must assess and mitigate ESG risks while leveraging opportunities for growth.',
    'Boards play a crucial role in embedding ESG into corporate DNA.',
    'ESG reporting is no longer optional – it is essential for investor confidence and regulatory compliance.',
    'Practical application of ESG leadership principles to real-world boardroom challenges.',
  ]

  return (
    <div>
      <section>
        <div className='relative'>
          <img src="https://storage.googleapis.com/testing_storage_cloudxier/cloudxier-images/25708212d8c5e137294fe7ba0ad7baae4ce269c444086d80c0cdc85e602a4f8e5184556e4a039c3d9b54f528894634179c4925716df56a1400eb9db3ef2ebed69675bd481f55d5c33cec7892e2dd4b7f0d3025a58549e877d0a6b4d6afb885378ef627df505e501bc008c01cfca3a2046fd1ecd43c1afcacd8a4/rez-file-1739775349512.jpg" alt="main" className='w-full max-h-40 sm:max-h-80 object-cover object-bottom' />
          <div className='absolute top-0 bottom-0 left-0 right-0 bg-app-orange/10 text-white'>
            <div className='app-main-container flex flex-col justify-center items-center h-full gap-4 sm:gap-8'>
              <p className='text-xl/[2rem] sm:text-3xl/[4rem] lg:text-5xl/[5rem] max-w-6xl px-8 font-semibold sm:font-bold text-center app-animation-translate-start'>Leadership in Sustainability</p>
            </div>
          </div>
        </div>
      </section>

      <section className='bg-app-dark-gray/10'>
        <div className='app-main-container space-y-8'>
          <p className='py-3 md:py-6 text-base/[1.75rem] md:text-lg/[2rem] xl:text-xl/[2.5rem] text-center app-animation-translate-start'>
            Our <b>Leadership for Education</b> program is designed to equip <b>educators, administrators, and academic leaders</b> with the knowledge and skills to integrate <b>Sustainability, ESG (Environmental, Social, and Governance), and Entrepreneurship</b> into educational leadership and curriculum development. Through <b>customized in-house training</b>, we help institutions foster future-ready leaders who can drive <b>positive environmental and social impact</b> while encouraging <b>innovation and sustainable business practices</b> in education.
          </p>
        </div>
      </section>

      <section className='bg-app-light-gray/10'>
        <div className='app-main-container space-y-8'>
          <div className='bg-app-dark-gray/10 mt-3 md:mt-6 flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-6 lg:space-y-0 p-4 sm:p-6 rounded-lg app-animation-translate-start'>
            <div className='app-animation-translate-start flex flex-row gap-3 cursor-pointer items-center' >
              <AlarmClockIcon width='40px' height='40px' />
              <p className='text-base sm:text-lg xl:text-xl'>2 Hours.</p>
            </div>
            <div className='app-animation-translate-start flex flex-row gap-3 cursor-pointer items-center' >
              <PersonWorkspaceIcon width='40px' height='40px' />
              <p className='text-base sm:text-lg xl:text-xl'>In-Person / Virtual.</p>
            </div>
            <div className='app-animation-translate-start flex flex-row gap-3 cursor-pointer items-center' >
              <PeopleIcon width='40px' height='40px' />
              <p className='text-base sm:text-lg xl:text-xl'>Board Members, Senior Executives.</p>
            </div>
          </div>
          <p className='pt-3 pb-2 md:pt-6 md:pb-4 text-xl md:text-2xl xl:text-4xl font-bold text-app-orange text-center app-animation-translate-start'>
            Workshop Objectives
          </p>
          <div className='pt-3 pb-4 md:pt-6 md:pb-8 space-y-6'>
            <p className='text-sm md:text-base xl:text-lg text-justify app-animation-translate-start'>
              This high-level session is designed to equip board members with essential knowledge and strategic insights on sustainability and ESG leadership. By the end of the training, participants will:
            </p>
            <ul className='ml-6 flex flex-col items-start text-sm md:text-base xl:text-lg space-y-2 md:space-y-4 app-animation-translate-start'>
              {objectives.map((objective, i) => {
                return (
                  <li key={i} className='px-2 list-disc'>
                    {objective}
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      </section>

      <section className='bg-app-dark-gray/10'>
        <div className='app-main-container space-y-8'>
          <p className='pt-3 pb-2 md:pt-6 md:pb-4 text-xl md:text-2xl xl:text-4xl font-bold text-app-orange text-center app-animation-translate-start'>
            Key Takeways
          </p>
          <div className='pt-3 pb-4 md:pt-6 md:pb-8'>
            <ul className='ml-6 flex flex-col items-start text-sm md:text-base xl:text-lg space-y-2 md:space-y-4 app-animation-translate-start'>
              {takeways.map((takeway, i) => {
                return (
                  <li key={i} className='px-2 list-disc'>
                    {takeway}
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      </section>

      <section className='bg-app-light-gray/10'>
        <div className='app-main-container space-y-8 pb-10 md:pb-14'>
          <p className='pt-3 pb-2 md:pt-6 md:pb-4 text-xl md:text-2xl xl:text-4xl font-bold text-app-orange text-center app-animation-translate-start'>
            Topics
          </p>
          <div className='grid grid-cols-1 gap-10 md:gap-14 pt-2 pb-6 md:pt-4 md:pb-12'>
            {leadership_contents.map((content, i) => {
              return (
                <div key={i} className='flex flex-row-reverse bg-app-dark-gray/10 rounded-lg app-animation-translate-start'>
                  <img className="w-1/2 aspect-video object-center object-cover rounded-r-lg" src={content.img_url} alt={content.title} />
                  <Accordion className='w-1/2 p-4 flex items-center' transition transitionTimeout={200}>
                    <AccordionItem header={content.title} key={i} order={i}>
                      <ul className='mt-2 ml-3 sm:ml-6 flex flex-col items-start text-sm sm:text-base md:text-lg xl:text-xl space-y-2'>
                        {content.descriptions.map((description, k) => {
                          return (
                            <li key={k} className='px-2 list-disc'>
                              {description}
                            </li>
                          )
                        })}
                      </ul>
                    </AccordionItem>
                  </Accordion>
                </div>
              )
            })}
          </div>
        </div>
      </section>
    </div>
  )
}

export default LeadershipSustainability